import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, UserCheck, FileText } from 'lucide-react';

const LeadFormSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('lead-form');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: UserCheck,
      title: "Schritt 1: Kontaktdaten",
      description: "Ihre Grunddaten, Anrede und Kontaktinformationen für die Angebotsstellung"
    },
    {
      icon: FileText, 
      title: "Schritt 2: Immobiliendetails & Heizung",
      description: "Angaben zu Ihrem Gebäude, aktueller Heizung und Projektzielen (optional)"
    },
    {
      icon: CheckCircle,
      title: "Schritt 3: Installationsadresse",
      description: "Straße, PLZ und Ort wo die Wärmepumpe installiert werden soll"
    }
  ];

  return (
    <section 
      id="lead-form" 
      className="relative py-8 sm:py-12 md:py-16 lg:py-20 overflow-visible"
      style={{
        background: 'linear-gradient(135deg, rgba(240,253,244,0.5) 0%, rgba(255,255,255,1) 50%, rgba(239,246,255,0.45) 100%)'
      }}
    >
      {/* SECTION NUMBERING */}
      <div className="absolute top-4 left-4 z-[60] bg-red-600 text-white px-3 py-2 rounded-lg font-bold text-sm sm:text-base shadow-lg hidden">
        SEKTION 5: LEAD-FORMULAR
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-40 left-20 w-80 h-80 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"
             style={{backgroundColor: '#91d33d'}}></div>
        <div className="absolute bottom-40 right-20 w-80 h-80 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"
             style={{backgroundColor: '#57c558', animationDelay: '4s'}}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-6 sm:mb-8 md:mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6 relative z-50 leading-tight">
              In 2 Minuten zu Ihrem 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green-dark to-brand-green relative z-50 mt-1 sm:mt-2">
                kostenlosen Angebot
              </span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-2">
              Einfach, schnell und unverbindlich – Ihr maßgeschneidertes Wärmepumpen-Angebot 
              inkl. Förderberechnung wartet auf Sie.
            </p>
          </div>

          {/* Process Steps */}
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 md:mb-12">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div
                  key={index}
                  className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200/50 hover:shadow-xl transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{
                    transitionDelay: `${200 + index * 100}ms`
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-brand-green to-brand-green-hover rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2 relative z-50">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Benefits - Hidden on small mobile to reduce content height */}
          <div className={`hidden sm:block bg-gradient-to-r from-brand-green-dark to-brand-green rounded-2xl p-4 sm:p-6 md:p-8 text-white transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 text-center">
              <div className="space-y-1 sm:space-y-2">
                <div className="text-2xl sm:text-3xl font-bold">100%</div>
                <div className="text-xs sm:text-sm text-green-100">kostenlos & unverbindlich</div>
              </div>
              <div className="space-y-1 sm:space-y-2">
                <div className="text-2xl sm:text-3xl font-bold">24h</div>
                <div className="text-xs sm:text-sm text-green-100">Rückmeldung garantiert</div>
              </div>
              <div className="space-y-1 sm:space-y-2">
                <div className="text-2xl sm:text-3xl font-bold">2.000+</div>
                <div className="text-xs sm:text-sm text-green-100">zufriedene Kunden</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadFormSection;